
import { useState, useCallback } from 'react';

interface UseMenuToggleReturn {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  open: () => void;
}

/**
 * Hook to manage menu toggle state with additional control methods
 * @param {boolean} initialState - Initial open state of the menu
 * @param {Function} onToggle - Optional callback to execute when menu is toggled
 * @returns {Object} Object containing menu state and control methods
 */
export function useMenuToggle(
  initialState = false,
  onToggle?: (isOpen: boolean) => void
): UseMenuToggleReturn {
  const [isOpen, setIsOpen] = useState(initialState);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const newState = !prev;
      if (onToggle) onToggle(newState);
      return newState;
    });
  }, [onToggle]);

  const close = useCallback(() => {
    setIsOpen(false);
    if (onToggle) onToggle(false);
  }, [onToggle]);

  const open = useCallback(() => {
    setIsOpen(true);
    if (onToggle) onToggle(true);
  }, [onToggle]);

  return { isOpen, toggle, close, open };
}
